import type { Credentials, LoginResponse, SignUpPayload, User } from "@/types/auth.ts"
import type { AIChatResponse } from "@/types/ai"
import type {
	Portfolio,
	Position,
	DividendEvent,
	CalendarEvent,
	EarningsEvent,
	SyncStatus,
	Transaction,
	BulkImportResult,
	PaginatedResponse,
	DividendReceived,
	DividendSummary,
	CashBalance,
	CashLedgerEntry,
} from "@/types/portfolio.ts"
import type { Stock } from "@/types/stock"
import type {
	FireSettings,
	FireDashboard,
	UpdateFireSettingsInput,
	FireShare,
	CreateFireShareInput,
} from "@/types/fire"
import type { TimeSeriesResponse, PerformanceSummary } from "@/types/performance"
import * as Sentry from "@sentry/react"

const API_URL = import.meta.env.VITE_API_URL ?? "https://api.beediv.com/v1"

// Client-side cache for forecasts to avoid redundant calls
const forecastCache = new Map<string, { data: DividendEvent[]; timestamp: number }>()
const CACHE_DURATION = 60 * 1000 // 1 minute

export interface Company {
	symbol: string
	exchange: string
	company_name: string
	sector?: string
	industry?: string
	description?: string
	website?: string
	currency?: string
	stock: {
		price: number
		changes_percentage?: number
		market_cap: number
		last_div?: number
	}
}

export type CompanySearchResult = Company

export interface DividendOverview {
	symbol: string
	exchange: string
	last_12m_dividend: number
	last_12m_dividend_growth: number
	dividend_growth_5y: number
	frequency?: string
	currency?: string
}

export interface BulkItem {
	symbol: string
	exchange?: string
}

export interface TickerLookupResult {
	symbol: string
	name: string
	exchange: string
	currency: string
}

export interface NotificationPreferences {
	user_id: string
	weekly_digest: boolean
	monthly_digest: boolean
}

export interface SafetyFactor {
	name: string
	raw_value: string | null
	score: number // 0.0–1.0 normalized
	max_score: number // weighted max contribution (0–100)
	points: number // score × max_score (now float)
	label: string
}

export interface SafetyDetail {
	symbol: string
	exchange: string
	score: number
	rating: "green" | "amber" | "red" | "gray"
	cut_probability: string // e.g. "~1%", "~5%", "~15%", "~30%", "~50%", or "" for gray
	factors: SafetyFactor[]
}

// Client-side cache for safety details to avoid redundant calls
const safetyCache = new Map<string, { data: SafetyDetail | null; timestamp: number }>()
const SAFETY_CACHE_DURATION = 5 * 60 * 1000 // 5 minutes

// Prevent concurrent refresh attempts
let refreshPromise: Promise<LoginResponse> | null = null

async function attemptTokenRefresh(): Promise<LoginResponse> {
	// Deduplicate concurrent refresh calls
	if (refreshPromise) return refreshPromise

	refreshPromise = (async () => {
		const refreshToken = localStorage.getItem("beediv-refresh-token")
		if (!refreshToken) throw new Error("No refresh token")

		const response = await fetch(`${API_URL}/auth/refresh`, {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({ refresh_token: refreshToken }),
		})

		return parsePayload<LoginResponse>(response)
	})()

	try {
		return await refreshPromise
	} finally {
		refreshPromise = null
	}
}

/**
 * Authenticated fetch wrapper with silent token refresh on 401.
 * - If the request returns 401, attempts to refresh the token
 * - On successful refresh: updates localStorage, dispatches event, retries request
 * - On refresh failure: clears tokens, dispatches session-expired event, throws
 */
async function apiFetch(url: string, options: RequestInit = {}, token?: string): Promise<Response> {
	if (token) {
		options.headers = {
			...(options.headers as Record<string, string>),
			Authorization: `Bearer ${token}`,
		}
	}

	const response = await fetch(url, options)

	if (response.status >= 500) {
		Sentry.addBreadcrumb({
			category: "api",
			message: `${options.method ?? "GET"} ${url} → ${response.status}`,
			level: "error",
		})
	}

	if (response.status === 401 && token) {
		try {
			const tokens = await attemptTokenRefresh()

			// Update localStorage
			localStorage.setItem("beediv-token", tokens.access_token)
			localStorage.setItem("beediv-refresh-token", tokens.refresh_token)

			// Notify AuthProvider to update React state
			window.dispatchEvent(
				new CustomEvent("beediv-token-refreshed", {
					detail: { access_token: tokens.access_token, refresh_token: tokens.refresh_token },
				}),
			)

			// Retry original request with new token
			const retryOptions = { ...options }
			retryOptions.headers = {
				...(retryOptions.headers as Record<string, string>),
				Authorization: `Bearer ${tokens.access_token}`,
			}
			return fetch(url, retryOptions)
		} catch {
			// Refresh failed — session is dead
			localStorage.removeItem("beediv-token")
			localStorage.removeItem("beediv-refresh-token")
			window.dispatchEvent(new Event("beediv-session-expired"))
			throw new Error("Session expired")
		}
	}

	return response
}

/**
 * Parse a JSON response and extract the typed payload.
 * Centralises the single unavoidable `as T` cast for untyped JSON.
 */
async function parsePayload<T>(response: Response): Promise<T> {
	const data = await response.json()
	if (!response.ok || data.error) {
		throw new Error(data.error?.message || data.error || "Request failed")
	}
	return data.payload as T
}

/**
 * Parse a paginated JSON response (payload + total count).
 */
async function parsePaginatedPayload<T>(response: Response): Promise<PaginatedResponse<T>> {
	const data = await response.json()
	if (!response.ok || data.error) {
		throw new Error(data.error?.message || data.error || "Request failed")
	}
	return { payload: data.payload as T[], total: data.total as number }
}

export interface ForexRates {
	base: string
	rates: Record<string, number>
	fetched_at: string
}

/**
 * Fetches authoritative USD-based forex rates from Hive.
 * Public endpoint — no auth required. These rates include EODHD-supplemented
 * currencies (e.g. TWD) that ECB-derived sources like frankfurter.dev lack.
 */
export async function getForexRates(): Promise<ForexRates> {
	const response = await fetch(`${API_URL}/forex/rates`)
	return parsePayload<ForexRates>(response)
}

export function triggerDownload(blob: Blob, filename: string): void {
	const url = URL.createObjectURL(blob)
	const link = document.createElement("a")
	link.href = url
	link.download = filename
	document.body.appendChild(link)
	link.click()
	document.body.removeChild(link)
	URL.revokeObjectURL(url)
}

function getToken(): string {
	return localStorage.getItem("beediv-token") || ""
}

export async function getCashBalance(portfolioId: string): Promise<CashBalance> {
	const res = await apiFetch(`${API_URL}/portfolios/${portfolioId}/cash`, {}, getToken())
	return parsePayload<CashBalance>(res)
}

export async function getCashLedger(
	portfolioId: string,
	limit = 50,
	offset = 0,
): Promise<{ entries: CashLedgerEntry[]; total: number }> {
	const res = await apiFetch(
		`${API_URL}/portfolios/${portfolioId}/cash/ledger?limit=${limit}&offset=${offset}`,
		{},
		getToken(),
	)
	const result = await parsePaginatedPayload<CashLedgerEntry>(res)
	return { entries: result.payload, total: result.total }
}

export async function depositCash(
	portfolioId: string,
	amount: number,
	currency: string,
	date: string,
	description?: string,
): Promise<CashLedgerEntry> {
	const res = await apiFetch(
		`${API_URL}/portfolios/${portfolioId}/cash`,
		{
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({ type: "DEPOSIT", amount, currency, date, description: description ?? "" }),
		},
		getToken(),
	)
	return parsePayload<CashLedgerEntry>(res)
}

export async function withdrawCash(
	portfolioId: string,
	amount: number,
	currency: string,
	date: string,
	description?: string,
): Promise<CashLedgerEntry> {
	const res = await apiFetch(
		`${API_URL}/portfolios/${portfolioId}/cash`,
		{
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({ type: "WITHDRAWAL", amount, currency, date, description: description ?? "" }),
		},
		getToken(),
	)
	return parsePayload<CashLedgerEntry>(res)
}

export async function deleteCashEntry(portfolioId: string, entryId: string): Promise<void> {
	const res = await apiFetch(`${API_URL}/portfolios/${portfolioId}/cash/${entryId}`, { method: "DELETE" }, getToken())
	if (!res.ok) {
		await parsePayload<never>(res)
	}
}

export async function updateCashSettings(portfolioId: string, enabled: boolean): Promise<void> {
	const res = await apiFetch(
		`${API_URL}/portfolios/${portfolioId}/cash/settings`,
		{
			method: "PUT",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({ enabled }),
		},
		getToken(),
	)
	if (!res.ok) {
		await parsePayload<never>(res)
	}
}

// Client-side cache for performance data (2 minute TTL)
const perfCache = new Map<string, { data: unknown; ts: number }>()
const PERF_CACHE_TTL = 2 * 60 * 1000

export async function getPerformanceTimeseries(period: string, portfolioId?: string): Promise<TimeSeriesResponse> {
	const key = `${portfolioId ?? "all"}:${period}:timeseries`
	const cached = perfCache.get(key)
	if (cached && Date.now() - cached.ts < PERF_CACHE_TTL) return cached.data as TimeSeriesResponse

	const params = new URLSearchParams({ range: period })
	if (portfolioId) params.set("portfolio_id", portfolioId)

	const res = await apiFetch(`${API_URL}/performance/timeseries?${params}`, {}, getToken())
	const result = await parsePayload<TimeSeriesResponse>(res)
	perfCache.set(key, { data: result, ts: Date.now() })
	return result
}

export async function getPerformanceSummary(period: string, portfolioId?: string): Promise<PerformanceSummary> {
	const key = `${portfolioId ?? "all"}:${period}:summary`
	const cached = perfCache.get(key)
	if (cached && Date.now() - cached.ts < PERF_CACHE_TTL) return cached.data as PerformanceSummary

	const params = new URLSearchParams({ range: period })
	if (portfolioId) params.set("portfolio_id", portfolioId)

	const res = await apiFetch(`${API_URL}/performance/summary?${params}`, {}, getToken())
	const result = await parsePayload<PerformanceSummary>(res)
	perfCache.set(key, { data: result, ts: Date.now() })
	return result
}

export async function refreshPerformanceCache(period: string, portfolioId?: string): Promise<void> {
	const params = new URLSearchParams({ range: period })
	if (portfolioId) params.set("portfolio_id", portfolioId)

	const res = await apiFetch(`${API_URL}/performance/refresh?${params}`, { method: "POST" }, getToken())
	await parsePayload<unknown>(res)
	perfCache.clear()
}

export async function aiChat(
	message: string,
	portfolioId: string,
	token: string,
): Promise<AIChatResponse> {
	const response = await apiFetch(
		`${API_URL}/ai/chat`,
		{
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({ message, portfolio_id: portfolioId }),
		},
		token,
	)

	if (response.status === 402) {
		const data = await response.json().catch(() => ({}))
		const msg =
			(data as { error?: { message?: string } }).error?.message ||
			"Monthly AI limit reached. Upgrade to Pro for unlimited access."
		const err = new Error(msg)
		;(err as Error & { isQuotaError: boolean }).isQuotaError = true
		throw err
	}

	return parsePayload<AIChatResponse>(response)
}

export async function updatePortfolioSettings(
	portfolioId: string,
	settings: {
		drip_default?: "ON" | "OFF"
		buy_fee?: number
		buy_fee_flat?: number
		sell_fee?: number
		drip_incurs_fee?: boolean
		tax_withholding_rate?: number
	},
	token: string,
): Promise<Portfolio> {
	const response = await apiFetch(`${API_URL}/portfolios/${portfolioId}/settings`, {
		method: "PUT",
		headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
		body: JSON.stringify(settings),
	})
	return parsePayload<Portfolio>(response)
}

export async function updatePositionDrip(
	portfolioId: string,
	ticker: string,
	exchange: string,
	config: {
		drip_setting: "ON" | "OFF" | "INHERIT"
		tax_withholding_rate?: number
		clear_tax_override?: boolean
	},
	token: string,
): Promise<Position> {
	const response = await apiFetch(`${API_URL}/portfolios/${portfolioId}/positions/${encodeURIComponent(exchange)}:${encodeURIComponent(ticker)}/drip`, {
		method: "PUT",
		headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
		body: JSON.stringify(config),
	})
	return parsePayload<Position>(response)
}

export default {
	updateMe: async (payload: { display_currency?: string; email_theme?: string }, token: string): Promise<User> => {
		const response = await apiFetch(
			`${API_URL}/auth/me`,
			{
				method: "PATCH",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify(payload),
			},
			token,
		)
		return parsePayload<User>(response)
	},
	getMe: async (token: string): Promise<User> => {
		const response = await apiFetch(`${API_URL}/auth/me`, {}, token)
		return parsePayload<User>(response)
	},
	socialLogin: async (
		provider: "google" | "apple",
		credential: string,
		userInfo?: { first_name?: string; last_name?: string },
	): Promise<LoginResponse> => {
		const response = await fetch(`${API_URL}/auth/social`, {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({ provider, credential, ...userInfo }),
		})
		return parsePayload<LoginResponse>(response)
	},
	login: async (credentials: Credentials): Promise<LoginResponse> => {
		const response = await fetch(`${API_URL}/auth/login`, {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify(credentials),
		})

		return parsePayload<LoginResponse>(response)
	},
	signup: async (signUpPayload: SignUpPayload): Promise<LoginResponse> => {
		const response = await fetch(`${API_URL}/auth/signup`, {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify(signUpPayload),
		})

		return parsePayload<LoginResponse>(response)
	},
	getPortfolios: async (token: string, _userId?: string, id?: string): Promise<Portfolio[]> => {
		let url = `${API_URL}/portfolios`
		if (id) {
			url += `?id=${id}`
		}

		const response = await apiFetch(url, { method: "GET" }, token)
		return parsePayload<Portfolio[]>(response)
	},
	createPortfolio: async (payload: { name: string }, token: string): Promise<Portfolio> => {
		const response = await apiFetch(
			`${API_URL}/portfolios`,
			{
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify(payload),
			},
			token,
		)

		return parsePayload<Portfolio>(response)
	},
	deletePortfolio: async (id: string, token: string): Promise<void> => {
		const response = await apiFetch(
			`${API_URL}/portfolios/${id}`,
			{
				method: "DELETE",
			},
			token,
		)

		await parsePayload<unknown>(response)
	},
	updatePortfolio: async (id: string, payload: { name: string }, token: string): Promise<Portfolio> => {
		const response = await apiFetch(
			`${API_URL}/portfolios?id=${id}`,
			{
				method: "PUT",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify(payload),
			},
			token,
		)

		return parsePayload<Portfolio>(response)
	},
	searchCompanies: async (query: string): Promise<CompanySearchResult[]> => {
		const response = await fetch(`${API_URL}/companies/search?q=${query}`)
		return parsePayload<CompanySearchResult[]>(response)
	},
	getDividendOverview: async (symbol: string, exchange: string): Promise<DividendOverview> => {
		const response = await fetch(`${API_URL}/dividends/overview?symbol=${symbol}&exchange=${exchange}`)
		return parsePayload<DividendOverview>(response)
	},
	getBulkCompanyInformation: async (items: BulkItem[]): Promise<Company[]> => {
		const response = await fetch(`${API_URL}/companies/bulk`, {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({ items }),
		})
		return parsePayload<Company[]>(response)
	},
	getBulkDividendOverviews: async (items: BulkItem[]): Promise<DividendOverview[]> => {
		const response = await fetch(`${API_URL}/dividends/bulk`, {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({ items }),
		})
		return parsePayload<DividendOverview[]>(response)
	},
	getBulkStocks: async (items: BulkItem[]): Promise<Stock[]> => {
		const BATCH_SIZE = 50
		const results: Stock[] = []

		for (let i = 0; i < items.length; i += BATCH_SIZE) {
			const batch = items.slice(i, i + BATCH_SIZE)

			const [companiesResponse, overviewsResponse] = await Promise.all([
				fetch(`${API_URL}/companies/bulk`, {
					method: "POST",
					headers: { "Content-Type": "application/json" },
					body: JSON.stringify({ items: batch }),
				}).then((res) => res.json()),
				fetch(`${API_URL}/dividends/bulk`, {
					method: "POST",
					headers: { "Content-Type": "application/json" },
					body: JSON.stringify({ items: batch }),
				}).then((res) => res.json()),
			])

			if (companiesResponse.error)
				throw new Error(companiesResponse.error.message || "Failed to fetch bulk companies")
			if (overviewsResponse.error)
				throw new Error(overviewsResponse.error.message || "Failed to fetch bulk dividends")

			const companies = companiesResponse.payload as Company[]
			const overviews = overviewsResponse.payload as DividendOverview[]

			const batchStocks = batch
				.map((item) => {
					const company = companies.find(
						(c) => c.symbol === item.symbol && (c.exchange === item.exchange || !item.exchange),
					)
					const overview = overviews.find(
						(o) => o.symbol === item.symbol && (o.exchange === item.exchange || !item.exchange),
					)

					if (!company) return null

					const stock: Stock = {
						ticker: company.symbol,
						name: company.company_name,
						exchange: company.exchange,
						price: company.stock.price || 0,
						marketCap: (company.stock.market_cap || 0).toString(),
						sector: company.sector || "Unknown",
						dividendPerShare: overview?.last_12m_dividend || company.stock.last_div || 0,
						dividendYield:
							(company.stock.price || 0) > 0
								? ((overview?.last_12m_dividend || company.stock.last_div || 0) /
										(company.stock.price || 1)) *
									100
								: 0,
						dividendGrowth5y:
							(overview?.dividend_growth_5y ?? overview?.last_12m_dividend_growth ?? 0) * 100,
						frequency: (overview?.frequency || "") as Stock["frequency"],
						currency: company.currency,
					}
					return stock
				})
				.filter((s): s is Stock => s !== null)

			results.push(...batchStocks)
		}

		return results
	},
	getStockByTicker: async (ticker: string): Promise<Stock> => {
		const results = await fetch(`${API_URL}/companies/search?q=${ticker}`).then((res) => res.json())
		if (!results.payload || results.payload.length === 0) {
			throw new Error(`Stock ${ticker} not found`)
		}

		// Find the exact match or take the first one
		const r = results.payload.find((c: CompanySearchResult) => c.symbol === ticker) || results.payload[0]

		try {
			const overviewResponse = await fetch(
				`${API_URL}/dividends/overview?symbol=${r.symbol}&exchange=${r.exchange}`,
			).then((res) => res.json())
			const overview = overviewResponse.payload as DividendOverview

			return {
				ticker: r.symbol,
				name: r.company_name,
				exchange: r.exchange,
				price: r.stock.price || 0,
				marketCap: (r.stock.market_cap || 0).toString(),
				sector: r.sector || "Unknown",
				dividendPerShare: overview.last_12m_dividend || 0,
				dividendYield:
					(r.stock.price || 0) > 0 ? ((overview.last_12m_dividend || 0) / (r.stock.price || 1)) * 100 : 0,
				dividendGrowth5y: (overview.dividend_growth_5y ?? overview.last_12m_dividend_growth ?? 0) * 100,
				frequency: (overview.frequency || "") as Stock["frequency"],
				currency: r.currency,
			}
		} catch {
			return {
				ticker: r.symbol,
				name: r.company_name,
				exchange: r.exchange,
				price: r.stock.price || 0,
				marketCap: (r.stock.market_cap || 0).toString(),
				sector: r.sector || "Unknown",
				dividendPerShare: r.stock.last_div || 0,
				dividendYield: (r.stock.price || 0) > 0 ? ((r.stock.last_div || 0) / (r.stock.price || 1)) * 100 : 0,
				dividendGrowth5y: 0,
				frequency: "Quarterly" as const,
				currency: r.currency,
			}
		}
	},
	fetchPortfolioForecast: async (id: string, token: string): Promise<DividendEvent[]> => {
		const cacheKey = `${id}:${token}`
		const cached = forecastCache.get(cacheKey)
		if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
			return cached.data
		}

		const response = await apiFetch(
			`${API_URL}/portfolios/${id}/forecast`,
			{
				method: "GET",
			},
			token,
		)

		const events = await parsePayload<DividendEvent[]>(response)
		forecastCache.set(cacheKey, { data: events, timestamp: Date.now() })

		return events
	},
	// Transaction endpoints
	createTransaction: async (
		portfolioId: string,
		payload: {
			ticker: string
			exchange: string
			type: "BUY" | "SELL"
			shares: number
			price?: number
			date?: string
		},
		token: string,
	): Promise<Transaction> => {
		const response = await apiFetch(
			`${API_URL}/portfolios/${portfolioId}/transactions`,
			{
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify(payload),
			},
			token,
		)

		const result = await parsePayload<Transaction>(response)
		forecastCache.delete(`${portfolioId}:${token}`)
		perfCache.clear()
		return result
	},
	bulkAddTransactions: async (
		portfolioId: string,
		transactions: {
			ticker: string
			exchange: string
			type: "BUY" | "SELL"
			shares: number
			price: number
			date: string
		}[],
		token: string,
	): Promise<BulkImportResult> => {
		const response = await apiFetch(
			`${API_URL}/portfolios/${portfolioId}/transactions/bulk`,
			{
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({ transactions }),
			},
			token,
		)

		const result = await parsePayload<BulkImportResult>(response)
		forecastCache.delete(`${portfolioId}:${token}`)
		perfCache.clear()
		return result
	},
	getTransactions: async (
		portfolioId: string,
		ticker: string | null,
		token: string,
		limit?: number,
		offset?: number,
	): Promise<PaginatedResponse<Transaction>> => {
		const params = new URLSearchParams()
		if (ticker) params.set("ticker", ticker)
		if (limit !== undefined) params.set("limit", limit.toString())
		if (offset !== undefined) params.set("offset", offset.toString())

		const qs = params.toString()
		const url = `${API_URL}/portfolios/${portfolioId}/transactions${qs ? `?${qs}` : ""}`

		const response = await apiFetch(url, { method: "GET" }, token)
		return parsePaginatedPayload<Transaction>(response)
	},
	updateTransaction: async (
		portfolioId: string,
		transactionId: string,
		payload: {
			type?: "BUY" | "SELL"
			shares?: number
			price?: number
			date?: string
		},
		token: string,
	): Promise<Transaction> => {
		const response = await apiFetch(
			`${API_URL}/portfolios/${portfolioId}/transactions/${transactionId}`,
			{
				method: "PATCH",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify(payload),
			},
			token,
		)

		const result = await parsePayload<Transaction>(response)
		forecastCache.delete(`${portfolioId}:${token}`)
		perfCache.clear()
		return result
	},
	deleteTransaction: async (portfolioId: string, transactionId: string, token: string): Promise<void> => {
		const response = await apiFetch(
			`${API_URL}/portfolios/${portfolioId}/transactions/${transactionId}`,
			{
				method: "DELETE",
			},
			token,
		)

		await parsePayload<unknown>(response)
		forecastCache.delete(`${portfolioId}:${token}`)
		perfCache.clear()
	},
	lookupTickers: async (query: string): Promise<TickerLookupResult[]> => {
		const response = await fetch(`${API_URL}/tickers/lookup?q=${query}`)
		return parsePayload<TickerLookupResult[]>(response)
	},
	fetchCalendarEvents: async (
		from: string,
		to: string,
		token: string,
		portfolioId?: string,
	): Promise<CalendarEvent[]> => {
		const params = new URLSearchParams({ from, to })
		if (portfolioId) params.set("portfolio_id", portfolioId)

		const response = await apiFetch(`${API_URL}/calendar?${params.toString()}`, { method: "GET" }, token)
		return parsePayload<CalendarEvent[]>(response)
	},
	downloadTransactionsCSV: async (portfolioId: string, token: string): Promise<Blob> => {
		const response = await apiFetch(
			`${API_URL}/portfolios/${portfolioId}/transactions/export`,
			{ method: "GET" },
			token,
		)
		if (!response.ok) throw new Error("Export failed")
		return response.blob()
	},
	downloadDividendsCSV: async (portfolioId: string, token: string, from?: string, to?: string): Promise<Blob> => {
		const params = new URLSearchParams()
		if (from) params.set("from", from)
		if (to) params.set("to", to)
		const qs = params.toString()

		const response = await apiFetch(
			`${API_URL}/portfolios/${portfolioId}/dividends/export${qs ? `?${qs}` : ""}`,
			{ method: "GET" },
			token,
		)
		if (!response.ok) throw new Error("Export failed")
		return response.blob()
	},
	getCalendarSubscription: async (token: string): Promise<{ url: string; token: string }> => {
		const response = await apiFetch(`${API_URL}/calendar/subscription`, { method: "GET" }, token)
		return parsePayload<{ url: string; token: string }>(response)
	},
	regenerateCalendarSubscription: async (token: string): Promise<{ url: string; token: string }> => {
		const response = await apiFetch(`${API_URL}/calendar/subscription/regenerate`, { method: "POST" }, token)
		return parsePayload<{ url: string; token: string }>(response)
	},
	getDividendIncomeHistory: async (
		portfolioId: string,
		ticker: string,
		exchange: string,
		token: string,
	): Promise<DividendReceived[]> => {
		const response = await apiFetch(
			`${API_URL}/portfolios/${portfolioId}/positions/${encodeURIComponent(exchange)}:${encodeURIComponent(ticker)}/dividends`,
			{ method: "GET" },
			token,
		)
		return parsePayload<DividendReceived[]>(response)
	},
	getDividendSummary: async (portfolioId: string, token: string): Promise<DividendSummary> => {
		const response = await apiFetch(
			`${API_URL}/portfolios/${portfolioId}/dividends/summary`,
			{ method: "GET" },
			token,
		)
		return parsePayload<DividendSummary>(response)
	},
	fetchEarningsCalendar: async (
		from: string,
		to: string,
		token: string,
		portfolioId?: string,
	): Promise<EarningsEvent[]> => {
		const params = new URLSearchParams({ from, to })
		if (portfolioId) params.set("portfolio_id", portfolioId)

		const response = await apiFetch(`${API_URL}/earnings?${params.toString()}`, { method: "GET" }, token)
		return parsePayload<EarningsEvent[]>(response)
	},
	getSyncStatus: async (token: string): Promise<SyncStatus> => {
		const response = await apiFetch(`${API_URL}/sync-status`, { method: "GET" }, token)
		return parsePayload<SyncStatus>(response)
	},
	addTicker: async (
		symbol: string,
		exchange: string,
		token?: string,
		name?: string,
		currency?: string,
	): Promise<Company> => {
		const response = await apiFetch(
			`${API_URL}/tickers`,
			{
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({ symbol, exchange, name, currency }),
			},
			token,
		)

		return parsePayload<Company>(response)
	},
	getFireSettings: async (token: string): Promise<FireSettings> => {
		const response = await apiFetch(`${API_URL}/fire/settings`, { method: "GET" }, token)
		return parsePayload<FireSettings>(response)
	},
	updateFireSettings: async (settings: UpdateFireSettingsInput, token: string): Promise<FireSettings> => {
		const response = await apiFetch(
			`${API_URL}/fire/settings`,
			{
				method: "PUT",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify(settings),
			},
			token,
		)
		return parsePayload<FireSettings>(response)
	},
	getFireDashboard: async (token: string): Promise<FireDashboard> => {
		const response = await apiFetch(`${API_URL}/fire/dashboard`, { method: "GET" }, token)
		return parsePayload<FireDashboard>(response)
	},
	createFireShare: async (input: CreateFireShareInput, token: string): Promise<FireShare> => {
		const response = await apiFetch(
			`${API_URL}/fire/shares`,
			{
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify(input),
			},
			token,
		)
		return parsePayload<FireShare>(response)
	},
	deleteFireShare: async (id: string, token: string): Promise<void> => {
		const response = await apiFetch(
			`${API_URL}/fire/shares/${id}`,
			{
				method: "DELETE",
			},
			token,
		)
		if (!response.ok && response.status !== 204) {
			const data = await response.json().catch(() => ({}))
			throw new Error((data as { error?: { message?: string } }).error?.message || "Failed to delete share")
		}
	},
	getNotificationPreferences: async (token: string): Promise<NotificationPreferences> => {
		const response = await apiFetch(`${API_URL}/notifications/preferences`, { method: "GET" }, token)
		return parsePayload<NotificationPreferences>(response)
	},
	updateNotificationPreferences: async (
		input: { weekly_digest?: boolean; monthly_digest?: boolean },
		token: string,
	): Promise<NotificationPreferences> => {
		const response = await apiFetch(
			`${API_URL}/notifications/preferences`,
			{
				method: "PUT",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify(input),
			},
			token,
		)
		return parsePayload<NotificationPreferences>(response)
	},
	getSafetyDetail: async (symbol: string, exchange: string): Promise<SafetyDetail | null> => {
		const cacheKey = `${symbol}:${exchange}`
		const cached = safetyCache.get(cacheKey)
		if (cached && Date.now() - cached.timestamp < SAFETY_CACHE_DURATION) {
			return cached.data
		}
		const res = await fetch(`${API_URL}/safety/${encodeURIComponent(exchange)}:${encodeURIComponent(symbol)}`)
		if (res.status === 404) {
			safetyCache.set(cacheKey, { data: null, timestamp: Date.now() })
			return null
		}
		if (!res.ok) throw new Error(`Safety API error: ${res.status}`)
		const detail = await parsePayload<SafetyDetail>(res)
		safetyCache.set(cacheKey, { data: detail, timestamp: Date.now() })
		return detail
	},
}
